import { message } from 'antd';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Dual Token State (Access + Refresh)
 *
 * 문서 참고: coin-server/docs/frontend/REFRESH_TOKEN_MIGRATION_GUIDE.md
 */
interface TokenState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresIn: number | null; // 초 단위 (900 = 15분)
  tokenType: string; // "Bearer"

  // Actions
  updateTokens: (accessToken: string, refreshToken: string, expiresIn?: number) => void;
  removeTokens: () => void;
}

const useTokenStore = create<TokenState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      expiresIn: 900, // 기본 15분
      tokenType: 'Bearer',

      updateTokens: (accessToken, refreshToken, expiresIn = 900) =>
        set({ accessToken, refreshToken, expiresIn }),

      removeTokens: () =>
        set({ accessToken: null, refreshToken: null }),
    }),
    {
      name: 'token-storage', // localStorage 키
      storage: createJSONStorage(() => localStorage),
    }
  )
);

/**
 * Dual Token Hook
 *
 * Before (Legacy):
 * - const { token, mutate } = useToken();
 *
 * After (Standard):
 * - const { accessToken, refreshToken, updateTokens } = useToken();
 *
 * ⚠️ CRITICAL: SWR fetcher에서 사용 시 주의
 * - const { getLatestToken } = useToken();
 * - useSWR(key, async () => API.get(url, getLatestToken(), ...))
 * - 클로저 문제 방지: 항상 최신 토큰 사용
 */
export function useToken() {
  const accessToken = useTokenStore((state) => state.accessToken);
  const refreshToken = useTokenStore((state) => state.refreshToken);
  const expiresIn = useTokenStore((state) => state.expiresIn);
  const tokenType = useTokenStore((state) => state.tokenType);

  const updateTokens = useTokenStore((state) => state.updateTokens);
  const removeTokens = useTokenStore((state) => state.removeTokens);

  /**
   * ⚠️ CRITICAL: localStorage에서 최신 토큰 직접 읽기
   *
   * 문제: SWR fetcher가 클로저에 토큰을 캡처하면,
   *       Refresh 후에도 오래된 토큰 사용
   *
   * 해결: localStorage에서 매번 최신 토큰 읽기
   *
   * @example
   * // ❌ 나쁜 예: 클로저에 캡처됨
   * const { data: token } = useToken();
   * useSWR(key, async () => API.get(url, token, ...));
   *
   * // ✅ 좋은 예: 항상 최신 토큰
   * const { getLatestToken } = useToken();
   * useSWR(key, async () => API.get(url, getLatestToken(), ...));
   */
  const getLatestToken = (): string | null => {
    try {
      const storageData = localStorage.getItem('token-storage');
      if (!storageData) return null;

      const { state } = JSON.parse(storageData);
      return state?.accessToken || null;
    } catch (e) {
      console.error('[useToken] Failed to get latest token:', e);
      return accessToken; // Fallback to Zustand state
    }
  };

  // Access Token에서 사용자 정보 디코드
  const userid = useMemo(() => {
    if (!accessToken) return null;
    try {
      const decodedToken: any = jwtDecode(accessToken);
      // JWT 표준: 'sub' (subject) 필드 우선, 'id' fallback
      const id = decodedToken?.sub || decodedToken?.id;
      // 문자열이면 숫자로 변환
      return id ? Number(id) : null;
    } catch (e) {
      return null;
    }
  }, [accessToken]);

  const expire = useMemo(() => {
    if (!accessToken) return null;
    try {
      const decodedToken: any = jwtDecode(accessToken);
      return decodedToken?.exp || null;
    } catch (e) {
      return null;
    }
  }, [accessToken]);

  const level = useMemo(() => {
    if (!accessToken) return null;
    try {
      const decodedToken: any = jwtDecode(accessToken);
      return decodedToken?.level || null;
    } catch (e) {
      return null;
    }
  }, [accessToken]);

  // 계정 상태 체크
  useEffect(() => {
    if (level == -9) {
      message.error('Your account has been suspended.');
      removeTokens();
    } else if (level == -2) {
      message.error('Your account has been Rejected.');
      removeTokens();
    } else if (level == -10) {
      message.error('Your account has been Blacked.');
      removeTokens();
    } else if (level == -8) {
      message.error('Your account is waiting for approval.');
      removeTokens();
    } else if (level == -1) {
      message.error('Your account is waiting for approval.');
      removeTokens();
    }
  }, [level, removeTokens]);

  return {
    // 새로운 Dual Token 필드
    accessToken,
    refreshToken,
    expiresIn,
    tokenType,

    // 사용자 정보
    userid,
    level,
    expire,

    // Actions
    updateTokens,
    removeTokens,

    // ⚠️ CRITICAL: SWR fetcher용 최신 토큰 getter
    getLatestToken,

    // Legacy 호환성 (기존 코드가 token 필드 사용 시)
    token: accessToken,
    data: accessToken,
    mutate: (value: string | null) => {
      // Legacy: 단일 토큰만 업데이트
      if (value) {
        updateTokens(value, value); // 임시로 같은 값 사용
      } else {
        removeTokens();
      }
    },
  };
}
